<template>
  <spinner v-if="$apollo.loading" />
  <div v-else>
    <h1 class="title-screen">{{ adminText('workflowsAll.r1_invoices_8bf2e93', 'R1 Invoices') }}</h1>
    <div class="invoice-toolbar">
      <text-field
        :label="adminText('workflowsAll.search_invoices_0e6f44a', 'Search invoices')"
        class="invoice-search"
        prepend-icon="search"
        @input="onInputChange"
      />
      <transition name="selection-actions">
        <div v-if="tableValue.length" class="bulk-download-actions">
          <div class="selected-count">
            <span class="selected-count-value">{{ workflowLabel(tableValue.length) }}</span>
            <span>{{ adminText('workflowsAll.selected_835f3b5', 'selected') }}</span>
          </div>
          <main-button
            class="bulk-download-button"
            depressed
            :disabled="isDownloadingSelected"
            :loading="isDownloadingSelected"
            @click="downloadSelectedInvoices"
          >
            <v-icon small>archive</v-icon>
            <span>{{ adminText('workflowsAll.download_pdfs_d30b169', 'Download PDFs') }}</span>
          </main-button>
        </div>
      </transition>
    </div>

    <v-card class="table elevation-0">
      <v-data-table
        v-model="tableValue"
        :headers="workflowUi(headers)"
        :items="workflowUi(filteredInvoices)"
        :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPageItems"
        :rows-per-page-text="adminText('workflowsAll.rows_per_page_79df325', 'Rows per page:')"
        item-key="orderId"
        select-all
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td class="selection-cell">
              <v-checkbox
                :input-value="props.selected"
                color="primary"
                hide-details
                @change="props.selected = $event"
              />
            </td>
            <td
              v-for="header in headers"
              :key="header.value || 'actions'"
              :class="{ 'actions-cell': !header.value }"
            >
              <div v-if="header.value">
                <span class="data-table-text">{{ header.value === 'createdAt' ? formatDateTime(props.item.createdAt) : workflowLabel(props.item[header.value]) }}</span>
              </div>
              <div v-else>
                <div class="invoice-actions">
                  <router-link
                    class="invoice-action invoice-action--view"
                    target="_blank"
                    :to="{
                      name: routes.ungrouped.invoicepdf,
                      params: {
                        orderUUID: props.item.orderUUID,
                      },
                    }"
                  >
                    <v-icon small>visibility</v-icon>
                    <span>{{ adminText('workflowsAll.view_69bd4ef', 'View') }}</span>
                  </router-link>
                  <v-btn
                    flat
                    small
                    class="invoice-action invoice-action--download"
                    :loading="isInvoiceDownloading(props.item.orderId)"
                    :disabled="isDownloadingSelected || isInvoiceDownloading(props.item.orderId)"
                    @click="downloadInvoice(props.item)"
                  >
                    <v-icon small>file_download</v-icon>
                    <span>{{ adminText('workflowsAll.download_pdf_98e5ef0', 'Download PDF') }}</span>
                  </v-btn>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
    <p v-if="downloadError" class="download-error">{{ workflowLabel(downloadError) }}</p>
  </div>
</template>
<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { downloadEventLegalEntityInvoices, getEventLegalEntityInvoices } from '@/http'

export default {
  mixins: [WorkflowDisplay],
  name: 'legal-entity-invoices-container',
  props: {
    organizationSlug: {
      type: String,
      default: '',
    },
    eventId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tableValue: [],
      invoices: [],
      filteredInvoices: [],
      pagination: {},
      rowsPerPageItems: [5, 10, 25, { text: 'workflowsAll.all_6a72085', value: -1 }],
      downloadingOrderIds: [],
      isDownloadingSelected: false,
      downloadError: '',
      headers: [
        {
          text: 'workflowsAll.invoice_number_254500c',
          align: 'left',
          value: 'invoiceID',
        },
        {
          text: 'workflowsAll.payment_date_33ab083',
          align: 'left',
          value: 'createdAt',
        },
        {
          text: 'workflowsAll.customer_0e85749',
          align: 'left',
          value: 'legalEntity',
        },
        {
          text: 'workflowsAll.actions_c3cd636',
          align: 'right',
          sortable: false,
        },
      ],
    }
  },
  created() {
    this.getLegalEntityInvoices()
  },
  methods: {
    formatDateTime(date) {
      return this.workflowDate(date, 'DD. MMMM. YYYY HH:mm')
    },
    async getLegalEntityInvoices() {
      const { legalEntityInvoices } = await getEventLegalEntityInvoices(this.eventId)

      this.invoices = legalEntityInvoices.map(invoice => {
        return {
          orderId: invoice.id,
          invoiceID: invoice.invoiceNumber != null ? invoice.invoiceNumber : invoice.id,
          createdAt: invoice.createdAt,
          legalEntity: `${invoice.legalEntity.name}, OIB: ${invoice.legalEntity.OIB}`,
          orderUUID: invoice.uuid,
        }
      })
      this.filteredInvoices = this.invoices
    },
    onInputChange(t) {
      const term = (t || '').trim().toLowerCase()

      this.filteredInvoices = this.invoices.filter(inv => {
        return (
          String(inv.invoiceID).toLowerCase().includes(term) ||
          inv.legalEntity.toLowerCase().includes(term)
        )
      })
      this.pagination = { ...this.pagination, page: 1 }
    },
    isInvoiceDownloading(orderId) {
      return this.downloadingOrderIds.includes(orderId)
    },
    getDownloadFilename(response, fallback) {
      const disposition = response.headers['content-disposition']
      if (!disposition) return fallback

      const match = disposition.match(/filename="?([^";]+)"?/)

      return match && match[1] ? match[1] : fallback
    },
    saveDownload(response, type, fallbackFilename) {
      const url = window.URL.createObjectURL(new Blob([response.data], { type }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', this.getDownloadFilename(response, fallbackFilename))
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    async getDownloadError(error) {
      const fallback = 'workflowsAll.failed_to_download_invoice_1b5173f'
      const data = error && error.response && error.response.data
      if (!data) return (error && error.message) || fallback
      if (!(data instanceof Blob)) return data.message || fallback

      try {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsText(data)
        })
        const parsed = JSON.parse(text)

        return parsed.error || parsed.message || fallback
      } catch (parseError) {
        return fallback
      }
    },
    async downloadInvoice(invoice) {
      this.downloadError = ''
      this.downloadingOrderIds.push(invoice.orderId)

      try {
        const response = await downloadEventLegalEntityInvoices(this.eventId, [invoice.orderId])
        this.saveDownload(response, 'application/pdf', `r1-invoice-${invoice.orderId}.pdf`)
      } catch (error) {
        this.downloadError = await this.getDownloadError(error)
      } finally {
        this.downloadingOrderIds = this.downloadingOrderIds.filter(id => id !== invoice.orderId)
      }
    },
    async downloadSelectedInvoices() {
      if (!this.tableValue.length) return

      this.downloadError = ''
      this.isDownloadingSelected = true

      try {
        const response = await downloadEventLegalEntityInvoices(
          this.eventId,
          this.tableValue.map(invoice => invoice.orderId),
          true,
        )
        this.saveDownload(response, 'application/zip', 'r1-invoices.zip')
      } catch (error) {
        this.downloadError = await this.getDownloadError(error)
      } finally {
        this.isDownloadingSelected = false
      }
    },
  },
}
</script>
<style scoped>
.invoice-toolbar {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 1rem;
  margin-bottom: 1.25rem;
  margin-top: 2rem;
}

@media screen and (min-width: 992px) {
  .invoice-toolbar {
    margin-top: 0;
  }
}

.invoice-search {
  flex: 0 1 440px;
  min-width: 0;
}

.invoice-search >>> .v-text-field {
  margin: 0;
  padding: 0;
}

.invoice-search >>> .v-input__slot {
  min-height: 48px;
  margin-bottom: 0;
  border: 1px solid #d3dde1;
  border-radius: 8px;
  background: #fff;
  box-shadow: none !important;
}

.invoice-search >>> .v-input__slot::before,
.invoice-search >>> .v-input__slot::after {
  display: none;
}

.invoice-search >>> .v-input--is-focused .v-input__slot {
  border-color: #0277bd;
  box-shadow: 0 0 0 3px rgba(2, 119, 189, 0.12) !important;
}

.invoice-search >>> .v-label {
  color: #7b868c;
  font-size: 14px;
}

.invoice-search >>> .v-icon {
  color: #607780;
}

.bulk-download-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  padding: 4px 4px 4px 0.75rem;
  border: 1px solid #c7e4f0;
  border-radius: 24px;
  background: #f0f9fc;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #236483;
  font-family: Raleway, sans-serif;
  font-size: 13px;
  font-weight: 700;
}

.selected-count-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #0277bd;
  color: #fff;
  font-size: 12px;
}

.bulk-download-button >>> .v-btn {
  min-width: 156px;
  height: 36px;
  margin: 0;
  border-radius: 20px;
  box-shadow: none;
  text-transform: none;
}

.bulk-download-button >>> .v-btn__content {
  gap: 0.45rem;
}

.bulk-download-button >>> .v-btn__content span {
  display: block;
  font-size: 14px;
  letter-spacing: 0;
}

.bulk-download-button >>> .v-btn--disabled {
  opacity: 1 !important;
  background: #e8edef !important;
  color: #8a979d !important;
}

.bulk-download-button >>> .v-btn--disabled .v-icon {
  color: #8a979d !important;
}

.selection-actions-enter-active,
.selection-actions-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.selection-actions-enter,
.selection-actions-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

@media screen and (max-width: 700px) {
  .invoice-toolbar {
    flex-wrap: wrap;
  }

  .invoice-search {
    flex-basis: 100%;
  }

  .bulk-download-actions {
    margin-left: 0;
  }
}

.document-container {
  display: flex;
  width: 331px;
  flex-direction: column;
  border-radius: 16px;
  border: 2px solid #d9d9d9;
  align-items: flex-start;
  flex-shrink: 0;
}

.document-header {
  border-radius: 16px 16px 0 0;
  display: flex;
  width: 100%;
  padding: 24px;
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
  background: #f5f5f5;
  gap: 1rem;
}

.document-type {
  color: #666;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.72px;
}

.table {
  border: 2px solid #f5f5f5;
  border-radius: 8px;
}

thead tr {
  background-color: #f5f5f5;
  height: 40px !important;
  border-radius: 8px 8px 0 0;
  border-bottom: none !important;
}

thead tr th {
  font-family: 'Raleway', sans-serif;
  font-size: 14px !important;
  font-weight: bold !important;
  line-height: 1.43 !important;
  color: #666666 !important;
}

tbody tr {
  border-bottom: none !important;
}

tbody tr:hover {
  background: rgba(2, 119, 189, 0.1) !important;
}

.data-table-text {
  font-size: 16px !important;
}

.actions-cell {
  width: 210px;
  white-space: nowrap;
}

.invoice-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

.invoice-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-width: 68px;
  height: 32px;
  margin: 0;
  padding: 0 0.65rem;
  border: 1px solid #d9e1e5;
  border-radius: 6px;
  color: #52636c;
  font-family: Raleway, sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
  text-decoration: none;
  text-transform: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.invoice-action--view:hover {
  border-color: #8ba6b2;
  background: #f3f7f8;
  color: #264653;
}

.invoice-action--download {
  min-width: 120px;
  border-color: #b9ddec;
  background: #edf8fc;
  color: #0277bd;
}

.invoice-action--download:hover {
  border-color: #6cb6d8;
  background: #dff3fb;
}

.invoice-action--download.v-btn--disabled {
  opacity: 1 !important;
  border-color: #e1e7e9;
  background: #f5f7f8 !important;
  color: #9ba8ad !important;
}

.invoice-action--download.v-btn--disabled .v-icon {
  color: #9ba8ad !important;
}

@media screen and (max-width: 700px) {
  .actions-cell {
    width: auto;
  }

  .invoice-action {
    min-width: 32px;
    padding: 0 0.5rem;
  }

  .invoice-action span {
    display: none;
  }
}

.selection-cell {
  width: 48px;
}

.selection-cell .v-input {
  margin: 0;
  padding: 0;
}

.download-error {
  color: #db384c;
  margin-top: 1rem;
}

.v-datatable__actions {
  border-top: 2px solid #f5f5f5 !important;
  background: none !important;
  height: 40px;
  flex-wrap: nowrap !important;
}

.v-datatable__actions__select {
  width: 40px;
}

.v-datatable {
  height: 40px;
}

.v-datatable .theme--light.v-text-field:not(.v-input--has-state) .v-input__slot::before,
.v-datatable .theme--light.v-text-field:not(.v-input--has-state) .v-input__slot:hover::before,
.v-datatable
  .theme--light.v-text-field.v-input--is-focused:not(.v-input--has-state)
  .v-input__slot::before {
  border-color: transparent !important;
}
</style>
